import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import HomeLayout from "../layouts/home_layout";
import { useSession } from "next-auth/react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ISO_DATE_FORMAT, groupNewResourceLink } from "../../helpers/application";
import { useInView } from "react-intersection-observer";
import Loader from "../shared/loader";
import ImageIcon from "../shared/image_icon";
import Link from "next/link";
import HorizontalCalendar from "../home/horizontal_calendar";
import GroupedEventsList from "../events/grouped_events_list";
import dayjs from "dayjs";
import { getChallenges, getEvents } from "../../src/queries";
import Centered from "../shared/centered";
import { useRouter } from "next/router";
import flatten from "lodash/flatten";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import DummyMargin from "../shared/dummy_margin";

const EventsList = ({ token, group, onlyList }) => {
  const router = useRouter();
  const { t } = useTranslation("common");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const { ref, inView } = useInView();
  const { data: session } = useSession();
  const [events, setEvents] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [q, setQ] = useState(null);
  const [qChallenges, setQChallenges] = useState(null);

  const { data, isFetching, fetchNextPage } = useInfiniteQuery(
    ["users_events", session?.user?.id, q],
    ({ pageParam = 1 }) => getEvents({ pageParam: pageParam, q: q }),
    {
      getNextPageParam: (lastPage) => lastPage.pagination?.next_page ?? undefined,
      enabled: session?.user?.id !== undefined && selectedDate !== null && q !== null,
    }
  );

  const { data: challenges_data } = useInfiniteQuery(
    ["users_challenges", session?.user?.id, qChallenges],
    ({ pageParam = 1 }) =>
      getChallenges({
        pageParam: pageParam,
        q: {
          group_token_or_groups_token_eq: token,
          ends_at_gteq: selectedDate.format(ISO_DATE_FORMAT),
        },
        per_page: 25,
      })
  );

  useEffect(() => {
    if (inView && !isFetching) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  useEffect(() => {
    if (!selectedDate) return;

    const formattedDate = selectedDate.format(ISO_DATE_FORMAT);
    setQ({ ...q, groups_token_or_admin_group_token_eq: token, starts_at_gteq: formattedDate, per_page: 5 });
  }, [selectedDate]);

  useEffect(() => {
    if (!data) return;

    const allEvents = data.pages.map((page) => page.data);
    const groupedEvents = Object.entries(groupBy(flatten(allEvents), "start_date"));

    setEvents(groupedEvents);
  }, [data]);

  useEffect(() => {
    if (!challenges_data) return;

    const allChallenges = challenges_data?.pages?.map((page) => page.data);
    setChallenges(Object.entries(groupBy(flatten(allChallenges), "starts_at")));
  }, [challenges_data]);

  return (
    <>
      {onlyList ? (
        <>
          <>
            <GroupedEventsList challenges={challenges} events={events} t={t} />

            <div className="text-center" ref={ref}>
              <Loader visible={isFetching} />
            </div>

            <DummyMargin />
          </>
        </>
      ) : (
        <HomeLayout
          title={t("groups.events.title")}
          navbarLeft={
            <li className="nav-item p-0">
              <a
                className="nav-link navbar-brand float-start ms-0 ps-0"
                onClick={() => router.replace(`/groups/${token}`)}
              >
                <ImageIcon name="back" />
              </a>
            </li>
          }
          navbarRight={
            group?.allow_challenges && group?.current_users_group?.is_admin ? (
              <>
                <li className="nav-item p-0 dropdown dropdown-menu-end dropdown-menu-lg-start">
                  <a className="nav-link navbar-brand float-end me-0 pe-0" data-bs-toggle="dropdown">
                    <ImageIcon name="plus-circle" />
                  </a>

                  <ul
                    className="dropdown-menu position-absolute"
                    style={{ overflow: "overlay", right: 0, left: "auto", zIndex: 1032 }}
                  >
                    <li>
                      <Link href={groupNewResourceLink(token, "events")}>
                        <a className="dropdown-item">
                          <ImageIcon name="hands" width={20} />
                          <span className="ms-1">{t("events.one")}</span>
                        </a>
                      </Link>
                    </li>
                    <li>
                      <Link href={groupNewResourceLink(token, "challenges")}>
                        <a className="dropdown-item">
                          <ImageIcon name="challenges" width={18} />
                          <span className="ms-1">{t("challenges.one")}</span>
                        </a>
                      </Link>
                    </li>
                  </ul>
                </li>
              </>
            ) : (
              <li className="nav-item p-0">
                <Link href={groupNewResourceLink(token, "events")}>
                  <a className="nav-link navbar-brand float-end me-0 pe-0">
                    <ImageIcon name="plus-circle" />
                  </a>
                </Link>
              </li>
            )
          }
        >
          <HorizontalCalendar
            onDateChange={(day) => setSelectedDate(day)}
            renderDaysBefore={30}
            renderDaysAfter={180}
            startDate={selectedDate}
          />

          <div className="pt-80">
            {events.length === 0 && challenges.length === 0 && !isFetching && (
              <Centered>
                <div className="text-center">
                  <ImageIcon name="calendar-big" alt="calendar" width={60} height={65} />
                  <div className="text-gray1 mt-12">{t("home.nothing")}</div>
                  <div className="text-center">
                    <Link href={groupNewResourceLink(token, "events")}>
                      <a className="text-center d-flex justify-content-center">
                        <ImageIcon name="plus-circle-blue" width={20} height={20} />
                        <span className="ms-1">{t("common:create")}</span>
                      </a>
                    </Link>
                  </div>
                </div>
              </Centered>
            )}

            <GroupedEventsList events={events} challenges={challenges} t={t} />
          </div>

          <div className="text-center" ref={ref}>
            <Loader visible={isFetching} />
          </div>
        </HomeLayout>
      )}
    </>
  );
};

export default EventsList;
